import type { Scene } from '@babylonjs/core';
import type {
  ActiveDelivery,
  DeliveryScore,
  Order,
  PlayerId,
  SessionMode,
} from '@roadworld/shared';
import type { ChunkManager } from '../world/ChunkManager.js';
import { latLngToWorld, type SessionFrame } from '../world/coords.js';
import { Beam, type BeamKind } from './Beam.js';

export interface ClientOrderPin {
  orderId: string;
  /** Order belongs to this player (we hold items)? Used to dim the pickup pin for others. */
  claimedByOther: boolean;
  lat: number;
  lng: number;
  kind: 'pickup' | 'dropoff';
  premium: boolean;
  pulsePhase: number;
}

interface ClientOrder {
  order: Order;
  pickupWorldX: number;
  pickupWorldZ: number;
  dropoffWorldX: number;
  dropoffWorldZ: number;
  pickupBeam: Beam | null;
  dropoffBeam: Beam | null;
}

export class ClientOrderManager {
  private scene: Scene;
  private chunks: ChunkManager;
  private frame: SessionFrame | null = null;
  private orders = new Map<string, ClientOrder>();
  private active: ActiveDelivery[] = [];
  private score: DeliveryScore = { sessionScore: 0, persistentMoney: 0 };
  private mode: SessionMode = 'freeroam';
  private isHostFlag = false;
  private onClock = false;
  private myId: PlayerId | null = null;
  private elapsed = 0;
  private beamIdCounter = 1;

  constructor(scene: Scene, chunks: ChunkManager) {
    this.scene = scene;
    this.chunks = chunks;
  }

  setSelfId(id: PlayerId): void {
    this.myId = id;
  }

  setFrame(frame: SessionFrame): void {
    this.frame = frame;
    for (const co of this.orders.values()) {
      this.refreshWorldCoords(co);
      if (co.pickupBeam) co.pickupBeam.dispose();
      if (co.dropoffBeam) co.dropoffBeam.dispose();
      co.pickupBeam = null;
      co.dropoffBeam = null;
    }
  }

  applyFullState(
    orders: Order[],
    mode: SessionMode,
    active: ActiveDelivery[],
    score: DeliveryScore,
    onClock: boolean,
    isHost: boolean,
  ): void {
    for (const co of this.orders.values()) {
      co.pickupBeam?.dispose();
      co.dropoffBeam?.dispose();
    }
    this.orders.clear();
    for (const o of orders) this.addOrder(o);
    this.mode = mode;
    this.active = active;
    this.score = score;
    this.onClock = onClock;
    this.isHostFlag = isHost;
  }

  addOrder(o: Order): void {
    const co: ClientOrder = {
      order: o,
      pickupWorldX: 0,
      pickupWorldZ: 0,
      dropoffWorldX: 0,
      dropoffWorldZ: 0,
      pickupBeam: null,
      dropoffBeam: null,
    };
    this.refreshWorldCoords(co);
    this.orders.set(o.id, co);
  }

  updateOrder(o: Order): void {
    const existing = this.orders.get(o.id);
    if (!existing) {
      this.addOrder(o);
      return;
    }
    existing.order = o;
  }

  removeOrder(id: string): void {
    const co = this.orders.get(id);
    if (!co) return;
    co.pickupBeam?.dispose();
    co.dropoffBeam?.dispose();
    this.orders.delete(id);
  }

  setActive(active: ActiveDelivery[]): void {
    this.active = active;
  }

  setScore(s: DeliveryScore): void {
    this.score = s;
  }

  setMode(m: SessionMode): void {
    this.mode = m;
  }

  setHost(isHost: boolean): void {
    this.isHostFlag = isHost;
  }

  setOnClock(on: boolean): void {
    this.onClock = on;
  }

  isHost(): boolean {
    return this.isHostFlag;
  }

  getActive(): ActiveDelivery[] {
    return this.active;
  }

  getScore(): DeliveryScore {
    return this.score;
  }

  getMode(): SessionMode {
    return this.mode;
  }

  getOnClock(): boolean {
    return this.onClock;
  }

  /** Per-frame: spawn/dispose beams based on chunk presence, advance pulse animation. */
  update(dt: number): void {
    this.elapsed += dt;
    const myActiveByOrder = new Map<string, ActiveDelivery>();
    for (const ad of this.active) myActiveByOrder.set(ad.orderId, ad);

    for (const co of this.orders.values()) {
      const myActive = myActiveByOrder.get(co.order.id);
      const meCompleted = this.myId
        ? co.order.completions.some((c) => c.playerId === this.myId)
        : false;
      // Pickup beam visible while I haven't yet picked up this order and haven't already
      // completed mine. (Per-player copy semantics — others picking it up doesn't hide my pin.)
      const showPickup = !myActive && !meCompleted;
      this.syncBeam(
        co,
        'pickup',
        showPickup,
        co.order.premium ? 'premium-pickup' : 'pickup',
        co.pickupWorldX,
        co.pickupWorldZ,
      );

      // Dropoff beam visible while I have an in-flight delivery for this order.
      const showDropoff = !!myActive;
      this.syncBeam(
        co,
        'dropoff',
        showDropoff,
        'dropoff',
        co.dropoffWorldX,
        co.dropoffWorldZ,
      );

      co.pickupBeam?.update(dt);
      co.dropoffBeam?.update(dt);
    }
  }

  /** Lat/lng + pin metadata for minimap rendering. */
  getMinimapPins(): ClientOrderPin[] {
    const out: ClientOrderPin[] = [];
    const myActiveByOrder = new Set(this.active.map((ad) => ad.orderId));

    for (const co of this.orders.values()) {
      const hasMine = myActiveByOrder.has(co.order.id);
      const meCompleted = this.myId
        ? co.order.completions.some((c) => c.playerId === this.myId)
        : false;
      const showPickup = !hasMine && !meCompleted;

      if (showPickup) {
        const claimedByOther = co.order.pickedUpBy.some((id) => id !== this.myId);
        out.push({
          orderId: co.order.id,
          claimedByOther,
          lat: co.order.pickup.lat,
          lng: co.order.pickup.lng,
          kind: 'pickup',
          premium: co.order.premium,
          pulsePhase: this.elapsed * 3 + hashStr(co.order.id),
        });
      }
      if (hasMine) {
        out.push({
          orderId: co.order.id,
          claimedByOther: false,
          lat: co.order.dropoff.lat,
          lng: co.order.dropoff.lng,
          kind: 'dropoff',
          premium: co.order.premium,
          pulsePhase: this.elapsed * 3 + hashStr(co.order.id) + 1.5,
        });
      }
    }
    return out;
  }

  *orderIter(): IterableIterator<Order> {
    for (const co of this.orders.values()) yield co.order;
  }

  // ---- internal ----

  private refreshWorldCoords(co: ClientOrder): void {
    if (!this.frame) return;
    const p = latLngToWorld({ lat: co.order.pickup.lat, lng: co.order.pickup.lng }, this.frame);
    const d = latLngToWorld({ lat: co.order.dropoff.lat, lng: co.order.dropoff.lng }, this.frame);
    co.pickupWorldX = p.x;
    co.pickupWorldZ = p.z;
    co.dropoffWorldX = d.x;
    co.dropoffWorldZ = d.z;
  }

  private syncBeam(
    co: ClientOrder,
    slot: 'pickup' | 'dropoff',
    shouldShow: boolean,
    kind: BeamKind,
    x: number,
    z: number,
  ): void {
    const has = slot === 'pickup' ? co.pickupBeam : co.dropoffBeam;
    if (!shouldShow) {
      if (has) {
        has.dispose();
        if (slot === 'pickup') co.pickupBeam = null;
        else co.dropoffBeam = null;
      }
      return;
    }
    if (!this.frame) return;
    const groundY = this.chunks.sampleHeight(x, z);
    if (groundY === null) {
      if (has) {
        has.dispose();
        if (slot === 'pickup') co.pickupBeam = null;
        else co.dropoffBeam = null;
      }
      return;
    }
    if (!has) {
      const beam = new Beam(this.scene, kind, x, groundY, z, this.beamIdCounter++);
      if (slot === 'pickup') co.pickupBeam = beam;
      else co.dropoffBeam = beam;
    } else {
      has.setPosition(x, groundY, z);
    }
  }
}

function hashStr(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return (h & 0xffff) / 0xffff;
}
